import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { ArrowLeft, GitCompareArrows, Sparkles, FileText } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { NarrativeBlock } from "./ReportView";

export default function Compare() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [reports, setReports] = useState([]);
  const [baseline, setBaseline] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/patients/${patientId}`);
        setPatient(data);
        const ready = (data.reports || []).filter((r) => r.status === "ready");
        setReports(ready);
        if (ready.length >= 2) {
          // Default: oldest as baseline, newest as follow-up
          const sorted = [...ready].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          setBaseline(sorted[0].report_id);
          setFollowUp(sorted[sorted.length - 1].report_id);
        }
      } catch {
        toast.error("Patient not found");
        navigate("/patients");
      }
    })();
  }, [patientId, navigate]);

  const run = async () => {
    if (!baseline || !followUp || baseline === followUp) {
      toast.error("Pick two different reports");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/reports/compare", {
        baseline_id: baseline,
        follow_up_id: followUp,
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Comparison failed");
    } finally {
      setRunning(false);
    }
  };

  if (!patient) return <Layout><div className="spinner mx-auto mt-10" /></Layout>;

  return (
    <Layout>
      <Link data-testid="compare-back-link" to={`/patients/${patientId}`} className="inline-flex items-center gap-1 text-sm mb-6" style={{ color: "var(--color-text-secondary)" }}>
        <ArrowLeft className="w-3.5 h-3.5" /> Back to {patient.name}
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <GitCompareArrows className="w-5 h-5" style={{ color: "var(--color-primary)" }} />
        <h1 className="font-display text-3xl md:text-4xl font-semibold">Compare reports</h1>
      </div>
      <p className="text-sm max-w-prose mb-8" style={{ color: "var(--color-text-secondary)" }}>
        Pick a baseline and a follow-up study for <span className="font-medium" style={{ color: "var(--color-text-primary)" }}>{patient.name}</span>.
        Claude Sonnet 4.6 will read both and write a longitudinal diff covering what changed, what stayed the same, and what to do next.
      </p>

      {reports.length < 2 ? (
        <div className="bg-white border rounded-lg p-12 text-center" style={{ borderColor: "var(--color-border)" }}>
          <FileText className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--color-secondary)" }} />
          <p className="font-display text-lg">Need at least 2 ready reports</p>
          <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>This patient has {reports.length} ready report{reports.length === 1 ? "" : "s"}. Upload another to compare.</p>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <ReportPicker label="Baseline (older)" value={baseline} onChange={setBaseline} options={reports} testid="compare-baseline-select" />
            <ReportPicker label="Follow-up (newer)" value={followUp} onChange={setFollowUp} options={reports} testid="compare-follow-up-select" />
          </div>
          <button
            data-testid="compare-run-button"
            onClick={run}
            disabled={running || !baseline || !followUp || baseline === followUp}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-white font-medium disabled:opacity-60"
            style={{ background: "var(--color-primary)" }}
          >
            <Sparkles className="w-4 h-4" /> {running ? "Reading both studies…" : "Generate comparison"}
          </button>
        </>
      )}

      {result && (
        <div className="mt-10 grid gap-6">
          <div className="grid md:grid-cols-2 gap-5">
            <ReportSummaryCard label="Baseline" data={result.baseline} />
            <ReportSummaryCard label="Follow-up" data={result.follow_up} />
          </div>

          <article className="bg-white p-8 md:p-12 border rounded-lg shadow-sm" style={{ borderColor: "var(--color-border)" }} data-testid="compare-result">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest font-display" style={{ color: "var(--color-text-secondary)" }}>Longitudinal comparison</div>
                <h2 className="font-display text-2xl mt-2">{patient.name}</h2>
              </div>
              {result.cached && (
                <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full" style={{ background: "var(--bg-canvas)", color: "var(--color-text-secondary)" }}>
                  Cached
                </span>
              )}
            </div>
            <div className="mt-6">
              <NarrativeBlock text={result.narrative} />
            </div>
            <div className="mt-8 pt-6 border-t text-xs" style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}>
              Generated {format(new Date(result.generated_at), "d MMM yyyy · HH:mm")} · AI-assisted, please verify against the source reports.
            </div>
          </article>
        </div>
      )}
    </Layout>
  );
}

function ReportPicker({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>{label}</label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]"
        style={{ borderColor: "var(--color-border)" }}
      >
        <option value="">Choose a report…</option>
        {options.map((r) => (
          <option key={r.report_id} value={r.report_id}>
            {format(new Date(r.created_at), "d MMM yyyy")} · {r.modality} · {r.original_filename}
          </option>
        ))}
      </select>
    </div>
  );
}

function ReportSummaryCard({ label, data }) {
  return (
    <div className="border rounded-lg p-5" style={{ borderColor: "var(--color-border)", background: "var(--bg-surface)" }}>
      <div className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>{label}</div>
      <div className="font-medium mt-1" style={{ color: "var(--color-text-primary)" }}>
        {data.modality} · {format(new Date(data.created_at), "d MMM yyyy")}
      </div>
      {data.indication && (
        <div className="text-xs mt-2" style={{ color: "var(--color-text-secondary)" }}>
          <span className="font-medium" style={{ color: "var(--color-text-primary)" }}>Indication:</span> {data.indication}
        </div>
      )}
      <Link to={`/reports/${data.report_id}`} className="inline-block mt-3 text-xs font-medium" style={{ color: "var(--color-primary)" }}>
        Open full report →
      </Link>
    </div>
  );
}
